import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { PSXPrintWrapper } from '../../../../../common/PSXPrintWrapper/PSXPrintWrapper';
import { usePrintPSX } from '../../../../../hooks/usePSXPrint/usePrintPSX';
import { PrintView } from '../Print/PrintView';
import {
  hideAskAIWidget,
  hideChat,
  hideFeedbackContainer,
  hideToasts,
  hideYourFeedbackForm,
  hideYourOpinionMattersPopup,
} from '../ShareDrawer/utils';

const PrintButtonStyled = styled(Button, {
  variants: {
    view: {
      mobile: {
        '&.abyss-button-root': {
          width: '100px',
          color: '$core.color.neutral.80',
          fontWeight: '$medium',
        },
      },
      desktop: {
        '&.abyss-button-root': {
          paddingRight: 0,
        },
      },
    },
  },
});

type Props = {
  locationForAnalytics: string;
  view: string;
  showMergedCompareAndShare?: boolean;
  selectedCount?: number;
  selectedProviders?: any[];
};

export const PrintButton = ({
  locationForAnalytics,
  view,
  showMergedCompareAndShare,
  selectedCount = 0,
  selectedProviders,
}: Props) => {
  const { printPSXPage, onPrintReady } = usePrintPSX();
  const { t } = useTranslation();

  return (
    <PrintButtonStyled
      css={{
        ...(view === 'desktop' &&
          showMergedCompareAndShare && { paddingRight: '20px' }), // Added for desktop view
        '.abyss-icon-symbol': {
          color:
            selectedCount < 2
              ? '#929496 !important'
              : '$core.color.neutral.100',
        },
      }}
      data-auto-testid="mapview-print-button"
      data-testid="mapview-print-button"
      isDisabled={selectedCount < 2}
      onClick={() => {
        if (showMergedCompareAndShare) {
          adobeLinkTrackEvent({
            name: 'share a pdf',
            location: 'Compare providers overlay',
            type: 'download',
          });
        } else {
          adobeLinkTrackEvent({
            name: 'print',
            location: locationForAnalytics,
            type: 'internal',
          });
        }
        hideFeedbackContainer(true);
        hideChat(true);
        hideToasts(true);
        hideAskAIWidget(true);
        hideYourFeedbackForm(true);
        hideYourOpinionMattersPopup(true);
        printPSXPage();
        hideFeedbackContainer(false);
        hideChat(false);
        hideToasts(false);
        hideAskAIWidget(false);
        hideYourFeedbackForm(false);
        hideYourOpinionMattersPopup(false);
      }}
      variant={showMergedCompareAndShare ? 'outline' : 'filled'}
      view={view}
    >
      {showMergedCompareAndShare ? (
        <IconSymbol data-testid="icon-symbol-download-icon" icon="download" />
      ) : (
        <IconSymbol
          css={{
            'abyss-icon': {
              marginRight: '$xs',
            },
          }}
          data-testid="icon-symbol-print-icon"
          icon="print"
        />
      )}
      {showMergedCompareAndShare
        ? t('SHARE_DRAWER_TEXT.SHARE_A_PDF')
        : t('SHARE_DRAWER_TEXT.PRINT')}
      {selectedProviders && (
        <PSXPrintWrapper onReady={onPrintReady}>
          <PrintView selectedProviderList={selectedProviders} />
        </PSXPrintWrapper>
      )}
    </PrintButtonStyled>
  );
};
